// SPDX-License-Identifier: AGPL-3.0-or-later

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, type ApiError } from './client.js';

/** API-safe view returned by GET /api/organizations/me/emr-connection. */
export interface EmrConnectionView {
  id: string;
  organizationId: string;
  adapterKind: 'generic-fhir' | 'epic' | 'fixture';
  displayName: string | null;
  baseUrl: string | null;
  hasCredentials: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface EmrConnectionResponse {
  connection: EmrConnectionView | null;
}

export interface UpsertEmrConnectionBody {
  adapterKind: 'generic-fhir' | 'epic' | 'fixture';
  displayName?: string | null;
  baseUrl?: string | null;
  /** Omit to preserve existing credentials when updating an existing row. */
  credentials?: { gatewayToken: string };
}

const QUERY_KEY = ['emr-connection'] as const;

export function useEmrConnection() {
  return useQuery<EmrConnectionResponse>({
    queryKey: QUERY_KEY,
    queryFn: () => apiFetch<EmrConnectionResponse>('/api/organizations/me/emr-connection'),
  });
}

export function useUpsertEmrConnection() {
  const qc = useQueryClient();
  return useMutation<EmrConnectionResponse, ApiError, UpsertEmrConnectionBody>({
    mutationFn: (body) =>
      apiFetch<EmrConnectionResponse>('/api/organizations/me/emr-connection', {
        method: 'PUT',
        body,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}

export function useDeleteEmrConnection() {
  const qc = useQueryClient();
  return useMutation<undefined, ApiError, undefined>({
    mutationFn: async () => {
      await apiFetch<undefined>('/api/organizations/me/emr-connection', { method: 'DELETE' });
      return undefined;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
  });
}
